import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { addMonths, eachDayOfInterval, endOfMonth, format, parseISO, startOfMonth, subMonths } from "date-fns";
import {
  AlertTriangle, ArrowRight, CalendarDays, CheckCircle2, ChevronLeft, ChevronRight, CircleDollarSign,
  IndianRupee, Search, WalletCards, X,
} from "lucide-react";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { SegmentedTabs } from "@/components/UXPrimitives";
import { DataPanel, EmptyState, MetricStrip, StatusPill, TableSurface } from "@/components/CommandSystem";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import api, { formatApiError } from "@/lib/api";
import { inr, inrPlain, fmtDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth";
import { hasAnyPermission } from "@/lib/permissions";
import { PLAN_FEATURES, hasPlanFeature } from "@/lib/planFeatures";
import { PermissionDenied } from "@/components/PermissionDenied";
import { PaginationFooter } from "@/components/PaginationFooter";

const today = new Date().toISOString().slice(0, 10);
const DEFAULT_RENT_PAGE_SIZE = 10;
const RECEIVABLE_PAGE_SIZE = 60;
const FINANCE_PAGE_API_CONFIG = { skipGlobalLoader: true };
const FINANCE_BACKGROUND_API_CONFIG = { skipGlobalLoader: true };

const STATUS_TABS = [
  { value: "outstanding", label: "Outstanding" },
  { value: "pending", label: "Pending" },
  { value: "overdue", label: "Overdue" },
  { value: "paid", label: "Paid" },
];

const RECEIVABLE_TABS = [
  { value: "outstanding", label: "Outstanding" },
  { value: "pending", label: "Pending" },
  { value: "overdue", label: "Overdue" },
  { value: "paid", label: "Paid" },
];

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function pagedItems(value) {
  return Array.isArray(value) ? value : asArray(value?.items);
}

/*
 * Semantic status colors — consistent across pills, dots, badges and text:
 *   paid / received  -> emerald (money in / settled)
 *   pending          -> amber   (waiting, not yet a problem)
 *   overdue          -> red     (action required)
 *   informational    -> blue    (brand / navigation / neutral highlights)
 */
function statusTone(status) {
  if (status === "paid") return "emerald";
  if (status === "overdue") return "rose";
  return "amber";
}

const STATUS_DOT = {
  paid: "bg-emerald-500",
  pending: "bg-amber-500",
  overdue: "bg-red-500",
};

function formatMonthLabel(rentMonth) {
  if (!rentMonth) return "";
  try {
    return format(parseISO(`${rentMonth}-01`), "MMM yyyy");
  } catch {
    return rentMonth;
  }
}

export function FinanceOperations() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();
  const initialQueue = searchParams.get("queue") === "rents" ? "rents" : "receivables";
  const initialDueParam = searchParams.get("due");
  const initialDue = initialDueParam === "today" ? today : null;
  const initialDueScope = initialDueParam === "until_today" ? "until_today" : "";
  const initialStatus = searchParams.get("status") === "all" ? "outstanding" : (searchParams.get("status") || "");
  const [dashboard, setDashboard] = useState(null);
  const [rents, setRents] = useState([]);
  const [receivables, setReceivables] = useState([]);
  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const [selectedDate, setSelectedDate] = useState(initialDue);
  const [dueScope, setDueScope] = useState(initialDueScope);
  const [statusFilter, setStatusFilter] = useState(initialQueue === "rents" && initialStatus ? initialStatus : "outstanding");
  const [queueView, setQueueView] = useState(initialQueue);
  const [receivableFilter, setReceivableFilter] = useState(initialQueue === "receivables" && initialStatus ? initialStatus : "outstanding");
  const [landownerInput, setLandownerInput] = useState("");
  const [landownerQuery, setLandownerQuery] = useState("");
  const [loadingRents, setLoadingRents] = useState(false);
  const [rentPage, setRentPage] = useState(1);
  const [rentPageSize, setRentPageSize] = useState(DEFAULT_RENT_PAGE_SIZE);
  const [rentTotal, setRentTotal] = useState(0);
  const [rentStatusTotals, setRentStatusTotals] = useState({ outstanding: 0, pending: 0, overdue: 0, paid: 0 });
  const [confirmEvent, setConfirmEvent] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const canViewReceivables = hasPlanFeature(user, PLAN_FEATURES.financeQueue) && hasAnyPermission(user, ["view_basic_revenue", "view_campaign_amounts", "view_profit_and_loss", "view_financial_reports"]);
  const canViewRents = hasPlanFeature(user, PLAN_FEATURES.financeAdvanced) && hasAnyPermission(user, ["view_rent_details", "view_expenses", "view_profit_and_loss", "view_financial_reports"]);
  const canViewWorkQueue = hasPlanFeature(user, PLAN_FEATURES.financeQueue) && (canViewReceivables || canViewRents);

  function rentDateParams(targetMonth) {
    if (dueScope === "until_today") {
      return { from: "1970-01-01", to: today };
    }
    if (selectedDate) {
      return { from: selectedDate, to: selectedDate };
    }
    return {
      from: format(startOfMonth(targetMonth), "yyyy-MM-dd"),
      to: format(endOfMonth(targetMonth), "yyyy-MM-dd"),
    };
  }

  function loadRents(targetMonth, landowner, pageNum = rentPage, rowsPerPage = rentPageSize, status = statusFilter) {
    if (!canViewRents) {
      setRents([]);
      setRentTotal(0);
      setRentStatusTotals({ outstanding: 0, pending: 0, overdue: 0, paid: 0 });
      return;
    }
    setLoadingRents(true);
    const params = new URLSearchParams(rentDateParams(targetMonth));
    params.set("limit", String(rowsPerPage));
    params.set("offset", String((pageNum - 1) * rowsPerPage));
    if (status) params.set("status", status);
    if (landowner) params.set("landowner", landowner);
    api.get(`/finance-ops/rent-calendar?${params}`, FINANCE_BACKGROUND_API_CONFIG)
      .then((res) => {
        const rows = pagedItems(res.data);
        setRents(rows);
        setRentTotal(Number(res.data?.total) || rows.length);
        setRentPage(pageNum);
      })
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail) || err.message))
      .finally(() => setLoadingRents(false));
  }

  function loadRentStatusTotals(targetMonth, landowner) {
    if (!canViewRents) {
      setRentStatusTotals({ outstanding: 0, pending: 0, overdue: 0, paid: 0 });
      return;
    }
    const baseParams = rentDateParams(targetMonth);
    const totalFor = (status) => {
      const params = new URLSearchParams(baseParams);
      params.set("limit", "1");
      params.set("offset", "0");
      if (status) params.set("status", status);
      if (landowner) params.set("landowner", landowner);
      return api.get(`/finance-ops/rent-calendar?${params}`, FINANCE_BACKGROUND_API_CONFIG)
        .then((res) => Number(res.data?.total) || pagedItems(res.data).length)
        .catch(() => 0);
    };
    Promise.all(STATUS_TABS.map((tab) => totalFor(tab.value))).then((totals) => {
      setRentStatusTotals(Object.fromEntries(STATUS_TABS.map((tab, index) => [tab.value, totals[index]])));
    });
  }

  function loadDashboardAndAudit() {
    if (!canViewWorkQueue) {
      setDashboard(null);
      setReceivables([]);
      setRents([]);
      setRentTotal(0);
      setRentStatusTotals({ outstanding: 0, pending: 0, overdue: 0, paid: 0 });
      return;
    }
    const params = new URLSearchParams(dueScope === "until_today"
      ? { from: "1970-01-01", to: today }
      : {
        from: format(startOfMonth(month), "yyyy-MM-dd"),
        to: format(endOfMonth(month), "yyyy-MM-dd"),
      });
    const receivableParams = new URLSearchParams(params);
    receivableParams.set("limit", String(RECEIVABLE_PAGE_SIZE));
    receivableParams.set("offset", "0");
    Promise.all([
      api.get(`/finance-ops/dashboard?${params}`, FINANCE_PAGE_API_CONFIG),
      canViewReceivables ? api.get(`/finance-ops/receivables?${receivableParams}`, FINANCE_PAGE_API_CONFIG) : Promise.resolve({ data: [] }),
    ]).then(([dash, rec]) => {
      const dashboardData = dash.data || {};
      setDashboard(dashboardData);
      setReceivables(canViewReceivables ? pagedItems(rec.data) : []);
    }).catch((err) => {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    });
  }

  useEffect(() => {
    if (!canViewReceivables && canViewRents) setQueueView("rents");
    loadDashboardAndAudit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, dueScope, canViewReceivables, canViewRents]);

  useEffect(() => {
    if (!canViewRents) return;
    loadRents(month, landownerQuery, rentPage, rentPageSize, statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, dueScope, selectedDate, statusFilter, landownerQuery, rentPage, rentPageSize, canViewRents]);

  useEffect(() => {
    if (!canViewRents) return;
    loadRentStatusTotals(month, landownerQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, dueScope, selectedDate, landownerQuery, canViewRents]);

  useEffect(() => {
    const queue = searchParams.get("queue");
    const due = searchParams.get("due");
    const status = searchParams.get("status") === "all" ? "outstanding" : searchParams.get("status");
    if (queue === "receivables" && canViewReceivables) {
      setQueueView("receivables");
      setReceivableFilter(status || "outstanding");
    }
    if (queue === "rents" && canViewRents) {
      setQueueView("rents");
      setStatusFilter(status || "outstanding");
      setRentPage(1);
    }
    if (due === "today") {
      setDueScope("");
      setSelectedDate(today);
      setMonth(startOfMonth(new Date()));
      setRentPage(1);
    } else if (due === "until_today") {
      setDueScope("until_today");
      setSelectedDate(null);
      setMonth(startOfMonth(new Date()));
      setRentPage(1);
    } else {
      setDueScope("");
    }
  }, [canViewReceivables, canViewRents, searchParamsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleSearch() {
    setLandownerQuery(landownerInput);
    setRentPage(1);
  }

  function jumpToToday() {
    setDueScope("");
    setMonth(startOfMonth(new Date()));
    setSelectedDate(today);
    setRentPage(1);
  }

  const eventsByDate = useMemo(() => {
    const map = {};
    const monthStart = startOfMonth(month);
    const monthEnd = endOfMonth(month);
    const todayDate = parseISO(today);
    rents.forEach((event) => {
      if (event.status === "overdue") {
        const dueDate = parseISO(event.due_date);
        if (Number.isNaN(dueDate.getTime())) return;
        let cursor = dueDate > monthStart ? dueDate : monthStart;
        const end = todayDate < monthEnd ? todayDate : monthEnd;
        while (!Number.isNaN(cursor.getTime()) && cursor <= end) {
          const key = format(cursor, "yyyy-MM-dd");
          const entry = map[key] || { pending: false, paid: false, overdue: false };
          entry.overdue = true;
          map[key] = entry;
          cursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 1);
        }
        return;
      }
      const markerDate = event.status === "paid" ? (event.paid_date || event.due_date) : event.due_date;
      const entry = map[markerDate] || { pending: false, paid: false, overdue: false };
      entry[event.status] = true;
      map[markerDate] = entry;
    });
    return map;
  }, [month, rents]);

  const dateFilteredRents = useMemo(
    () => (selectedDate
      ? rents.filter((r) => (r.status === "overdue" ? r.due_date <= selectedDate : (r.status === "paid" ? (r.paid_date || r.due_date) === selectedDate : r.due_date === selectedDate)))
      : rents),
    [rents, selectedDate]
  );

  const counts = useMemo(() => ({
    outstanding: rentStatusTotals.outstanding || dateFilteredRents.filter((r) => r.status !== "paid").length,
    pending: rentStatusTotals.pending || dateFilteredRents.filter((r) => r.status === "pending").length,
    overdue: rentStatusTotals.overdue || dateFilteredRents.filter((r) => r.status === "overdue").length,
    paid: rentStatusTotals.paid || dateFilteredRents.filter((r) => r.status === "paid").length,
  }), [dateFilteredRents, rentStatusTotals]);

  const visibleRents = dateFilteredRents;

  const monthDays = useMemo(() => eachDayOfInterval({
    start: startOfMonth(month),
    end: endOfMonth(month),
  }), [month]);

  const receivableCounts = useMemo(() => ({
    outstanding: receivables.filter((r) => r.payment_status !== "paid").length,
    pending: receivables.filter((r) => r.payment_status === "pending").length,
    overdue: receivables.filter((r) => r.payment_status === "overdue").length,
    paid: receivables.filter((r) => r.payment_status === "paid").length,
  }), [receivables]);

  const visibleReceivables = useMemo(
    () => {
      const dateScoped = selectedDate
        ? receivables.filter((r) => String(r.payment_due_date || "").slice(0, 10) === selectedDate)
        : receivables;
      if (receivableFilter === "outstanding") return dateScoped.filter((r) => r.payment_status !== "paid");
      return dateScoped.filter((r) => r.payment_status === receivableFilter);
    },
    [receivableFilter, receivables, selectedDate]
  );

  async function confirmMarkPaid() {
    if (!confirmEvent) return;
    setConfirming(true);
    try {
      await api.post(`/finance-ops/rent-calendar/${confirmEvent.hoarding_id}/${confirmEvent.rent_month}/mark-paid`, {
        amount: confirmEvent.amount,
        paid_date: today,
        status: "paid",
        notes: "Marked paid from finance calendar",
      }, FINANCE_BACKGROUND_API_CONFIG);
      toast.success("Rent marked as paid");
      setConfirmEvent(null);
      loadRents(month, landownerQuery, rentPage, rentPageSize, statusFilter);
      loadDashboardAndAudit();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setConfirming(false);
    }
  }

  return (
    <Layout title="Finance Operations" subtitle="Receivables, rent payable, and overdue payments">
      {!canViewWorkQueue ? (
        <PermissionDenied permission="view_financial_reports" />
      ) : (
      <>
      <MetricStrip
        metrics={[
          { label: "Receivable", value: inr(dashboard?.total_receivable || 0), icon: CircleDollarSign, tone: "blue", onClick: () => { setQueueView("receivables"); setReceivableFilter("outstanding"); } },
          { label: "Received", value: inr(dashboard?.total_received || 0), icon: WalletCards, tone: "emerald", onClick: () => { setQueueView("receivables"); setReceivableFilter("paid"); } },
          { label: "Rent Payable", value: inr(dashboard?.total_rent_payable || 0), icon: IndianRupee, tone: "amber", onClick: () => { setQueueView("rents"); setStatusFilter("outstanding"); } },
          { label: "Overdue Rent", value: inr(dashboard?.overdue_rent || 0), icon: AlertTriangle, tone: "rose", onClick: () => { setQueueView("rents"); setStatusFilter("overdue"); } },
          { label: "Open-Ended", value: dashboard?.open_ended_campaigns_count || 0, icon: CalendarDays, tone: "blue" },
          { label: "Unknown PO", value: dashboard?.unknown_po_value_campaigns_count || 0, icon: AlertTriangle, tone: "slate" },
        ]}
      />

      <DataPanel className="hx-finance-work-queue mt-5">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="font-display text-base font-semibold text-slate-950">Work Queue</h3>
            <p className="text-xs text-slate-500">Money coming in from clients, and rent going out to landowners.</p>
          </div>
          <div className="hx-tabs-scroll">
            <SegmentedTabs
              active={queueView}
              onChange={setQueueView}
              tabs={[
                canViewReceivables && { value: "receivables", label: "Client Receivables", meta: receivableCounts.outstanding },
                canViewRents && { value: "rents", label: "Rent Payables", meta: counts.outstanding || rentTotal },
              ].filter(Boolean)}
            />
          </div>
        </div>

        {queueView === "receivables" && canViewReceivables ? (
          <ReceivablesQueue
            rows={visibleReceivables}
            counts={receivableCounts}
            active={receivableFilter}
            onChange={setReceivableFilter}
          />
        ) : canViewRents ? (
          <>
        <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h3 className="font-display font-semibold text-slate-950">Rent Calendar</h3>
            <p className="text-xs text-slate-500">Pick a day to see what's due, or filter by status below.</p>
          </div>
          <div className="flex w-full flex-wrap gap-2 sm:w-auto">
            <div className="relative min-w-0 flex-1 sm:flex-none">
              <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={landownerInput}
                onChange={(e) => setLandownerInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
                placeholder="Search landowner"
                className="w-full pl-8 sm:w-52"
              />
            </div>
            <Button onClick={handleSearch} disabled={loadingRents} variant="outline">
              {loadingRents ? "Loading..." : "Search"}
            </Button>
          </div>
        </div>

        <div className="hx-rent-workspace">
          <HorizontalRentCalendar
            month={month}
            days={monthDays}
            selectedDate={selectedDate}
            eventsByDate={eventsByDate}
            onPreviousMonth={() => {
              setDueScope("");
              setMonth((current) => subMonths(current, 1));
              setSelectedDate(null);
              setRentPage(1);
            }}
            onNextMonth={() => {
              setDueScope("");
              setMonth((current) => addMonths(current, 1));
              setSelectedDate(null);
              setRentPage(1);
            }}
            onToday={jumpToToday}
            onSelectDate={(date) => {
              setDueScope("");
              setSelectedDate(date);
              setRentPage(1);
            }}
            onClearDate={() => {
              setSelectedDate(null);
              setRentPage(1);
            }}
          />

          <div className="hx-rent-details-block">
            <div className="mb-3 flex flex-col gap-2 xl:flex-row xl:items-center xl:justify-between">
              <div className="hx-tabs-scroll">
                <SegmentedTabs
                  active={statusFilter}
                  onChange={(value) => { setStatusFilter(value); setRentPage(1); }}
                  tabs={STATUS_TABS.map((tab) => ({ ...tab, meta: counts[tab.value] }))}
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                {loadingRents && (
                  <span className="hx-register-refresh" role="status">
                    <span aria-hidden="true" />
                    Updating
                  </span>
                )}
                <Select value={String(rentPageSize)} onValueChange={(v) => { setRentPageSize(Number(v)); setRentPage(1); }}>
                  <SelectTrigger className="hx-register-rows">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 per page</SelectItem>
                    <SelectItem value="25">25 per page</SelectItem>
                    <SelectItem value="50">50 per page</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <RentQueueTable
              rows={visibleRents}
              loading={loadingRents}
              selectedDate={selectedDate}
              onMarkPaid={setConfirmEvent}
            />
            <PaginationFooter
              page={rentPage}
              pageSize={rentPageSize}
              total={rentTotal}
              onPageChange={setRentPage}
              label="rent payables"
            />
          </div>
        </div>
          </>
        ) : null}

        <div className="hx-finance-summary-grid mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
          <Panel title="Branch Summary">
            <div className="hx-finance-summary-head">
              <span>Branch</span>
              <span>Receivable</span>
              <span>Rent</span>
              <span>P&amp;L</span>
            </div>
            {asArray(dashboard?.branch_wise).map((row) => (
              <div key={row.branch_type} className="hx-finance-summary-row">
                <span className="font-semibold capitalize text-slate-700">{row.branch_type}</span>
                <span>{inr(row.receivable)}</span>
                <span>{inr(row.rent_payable)}</span>
                <span className={cn("font-semibold", row.profit_loss >= 0 ? "text-emerald-700" : "text-red-600")}>{inr(row.profit_loss)}</span>
              </div>
            ))}
            {asArray(dashboard?.branch_wise).length === 0 && (
              <div className="py-6 text-center text-xs text-slate-400">No branch data for this period.</div>
            )}
          </Panel>
          <Panel title="Needs Attention">
            {asArray(dashboard?.campaigns_needing_attention).slice(0, 8).map((item) => (
              <div
                key={`${item.campaign_id}-${item.payment_status}`}
                className={cn(
                  "rounded-lg border p-2.5 text-xs",
                  item.payment_status === "overdue"
                    ? "border-red-100 bg-red-50/70"
                    : "border-amber-100 bg-amber-50/70"
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate font-semibold text-slate-900">{item.campaign_name}</div>
                    <div className="mt-0.5 text-slate-600">{item.client_name || "Client not set"}</div>
                  </div>
                  <StatusPill tone={statusTone(item.payment_status)}>{item.payment_status}</StatusPill>
                </div>
                <div className={cn(
                  "mt-1.5 font-semibold tabular-nums",
                  item.payment_status === "overdue" ? "text-red-700" : "text-amber-700"
                )}>
                  Pending {inr(item.pending_amount)}
                </div>
              </div>
            ))}
            {asArray(dashboard?.campaigns_needing_attention).length === 0 && (
              <div className="flex flex-col items-center gap-1 py-8 text-center">
                <CheckCircle2 size={18} className="text-emerald-500" />
                <span className="text-xs text-slate-400">Nothing urgent right now.</span>
              </div>
            )}
          </Panel>
        </div>
      </DataPanel>

      <AlertDialog open={!!confirmEvent} onOpenChange={(open) => { if (!open && !confirming) setConfirmEvent(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark rent as paid?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                {confirmEvent && (
                  <>
                    <div className="mt-1 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-slate-500">Landowner</span>
                        <span className="font-semibold text-slate-900">{confirmEvent.landowner_name || "Not set"}</span>
                      </div>
                      <div className="mt-1.5 flex items-center justify-between gap-3">
                        <span className="text-slate-500">Hoarding</span>
                        <span className="truncate font-medium text-slate-800">{confirmEvent.hoarding_name}</span>
                      </div>
                      <div className="mt-1.5 flex items-center justify-between gap-3">
                        <span className="text-slate-500">Rent month</span>
                        <span className="font-medium text-slate-800">{formatMonthLabel(confirmEvent.rent_month)}</span>
                      </div>
                      <div className="mt-2 flex items-center justify-between gap-3 border-t border-slate-200 pt-2">
                        <span className="text-slate-500">Amount</span>
                        <span className="font-display text-base font-bold tabular-nums text-slate-950">{inrPlain(confirmEvent.amount)}</span>
                      </div>
                    </div>
                    <p className="mt-3 text-xs text-slate-500">
                      Paid date will be recorded as today. This can't be undone from this screen.
                    </p>
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={confirming}>Cancel</AlertDialogCancel>
            <button
              onClick={confirmMarkPaid}
              disabled={confirming}
              className={cn(buttonVariants(), "bg-blue-600 hover:bg-blue-700 disabled:opacity-70")}
            >
              {confirming ? "Marking..." : "Yes, mark paid"}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </>
      )}
    </Layout>
  );
}

function HorizontalRentCalendar({
  month,
  days,
  selectedDate,
  eventsByDate,
  onPreviousMonth,
  onNextMonth,
  onToday,
  onSelectDate,
  onClearDate,
}) {
  return (
    <div className="hx-rent-calendar-panel">
      <div className="hx-rent-month-row">
        <Button
          size="icon"
          variant="outline"
          className="hx-rent-month-button"
          onClick={onPreviousMonth}
          aria-label="Previous month"
        >
          <ChevronLeft size={16} />
        </Button>
        <div className="hx-rent-month-title">
          <span className="hx-eyebrow">Rent calendar</span>
          <strong>{format(month, "MMMM yyyy")}</strong>
        </div>
        <div className="hx-rent-month-actions">
          <Button size="sm" variant="ghost" className="hx-rent-today-button" onClick={onToday}>Today</Button>
          <Button
            size="icon"
            variant="outline"
            className="hx-rent-month-button"
            onClick={onNextMonth}
            aria-label="Next month"
          >
            <ChevronRight size={16} />
          </Button>
        </div>
      </div>

      <div className="hx-rent-days-row" aria-label={`Rent days for ${format(month, "MMMM yyyy")}`}>
        {days.map((date) => {
          const key = format(date, "yyyy-MM-dd");
          const info = eventsByDate[key];
          const selected = selectedDate === key;
          return (
            <button
              key={key}
              type="button"
              className={cn("hx-rent-day-tile", selected && "is-selected", info?.overdue && "has-overdue")}
              onClick={() => onSelectDate(selected ? null : key)}
              aria-pressed={selected}
            >
              <span className="hx-rent-day-name">{format(date, "EEE")}</span>
              <span className="hx-rent-day-number">{format(date, "d")}</span>
              <span className="hx-rent-day-dots" aria-hidden="true">
                {info?.overdue && <span className={cn("hx-rent-day-dot", STATUS_DOT.overdue)} />}
                {info?.pending && <span className={cn("hx-rent-day-dot", STATUS_DOT.pending)} />}
                {info?.paid && <span className={cn("hx-rent-day-dot", STATUS_DOT.paid)} />}
              </span>
            </button>
          );
        })}
      </div>

      <div className="hx-rent-calendar-footer">
        <div className="hx-rent-calendar-legend">
          <span className="inline-flex items-center gap-1.5"><span className={cn("h-1.5 w-1.5 rounded-full", STATUS_DOT.pending)} /> Pending</span>
          <span className="inline-flex items-center gap-1.5"><span className={cn("h-1.5 w-1.5 rounded-full", STATUS_DOT.overdue)} /> Overdue</span>
          <span className="inline-flex items-center gap-1.5"><span className={cn("h-1.5 w-1.5 rounded-full", STATUS_DOT.paid)} /> Paid</span>
        </div>
        {selectedDate && (
          <div className="hx-rent-selected-date">
            <span>Showing {fmtDate(selectedDate)}</span>
            <button onClick={onClearDate} aria-label="Clear selected date">
              <X size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ForwardBadges({ carriedForward, forwardedTo }) {
  if (!carriedForward && !forwardedTo) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1 text-[10px] font-semibold">
      {carriedForward && (
        <span className="inline-flex items-center gap-0.5 rounded-full bg-blue-50 px-1.5 py-0.5 text-blue-700 ring-1 ring-inset ring-blue-100">
          carried forward
        </span>
      )}
      {forwardedTo && (
        <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-50 px-1.5 py-0.5 text-amber-700 ring-1 ring-inset ring-amber-100">
          <ArrowRight size={9} /> {formatMonthLabel(forwardedTo)}
        </span>
      )}
    </div>
  );
}

function ReceivablesQueue({ rows, counts, active, onChange }) {
  return (
    <div>
      <div className="hx-tabs-scroll mb-3">
        <SegmentedTabs
          active={active}
          onChange={onChange}
          tabs={RECEIVABLE_TABS.map((tab) => ({ ...tab, meta: counts[tab.value] }))}
        />
      </div>

      {/* Desktop / tablet table */}
      <TableSurface className="hx-finance-table hidden md:block">
        <table>
          <thead>
            <tr>
              <th>Campaign</th>
              <th>Client</th>
              <th>Due Date</th>
              <th className="text-right">Expected</th>
              <th className="text-right">Received</th>
              <th className="text-right">Pending</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.campaign_id} className={row.payment_status === "overdue" ? "hx-row-overdue" : undefined}>
                <td>
                  <div className="font-semibold text-slate-900">{row.campaign_name}</div>
                  <div className="text-xs text-slate-500">{row.campaign_code} · {row.branch_type || "main"}</div>
                  <ForwardBadges carriedForward={row.carried_forward} forwardedTo={row.forwarded_to} />
                </td>
                <td className="text-slate-700">{row.client_name || "Client not set"}</td>
                <td className="hx-date-cell">{row.payment_due_date ? fmtDate(row.payment_due_date) : "No due date"}</td>
                <td className="hx-money-cell text-right">{inr(row.expected_amount)}</td>
                <td className="text-right">
                  <div className="hx-money-cell text-emerald-700">{inr(row.received_in_period || 0)}</div>
                  <div className="text-[10px] tabular-nums text-slate-500">total {inr(row.received_amount || 0)}</div>
                </td>
                <td className={cn("hx-money-cell text-right font-semibold", row.payment_status === "overdue" ? "text-red-700" : "text-slate-950")}>
                  {inr(row.pending_amount)}
                </td>
                <td><StatusPill tone={statusTone(row.payment_status)}>{row.payment_status}</StatusPill></td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7}><EmptyState title="No client receivables" description="No receivables match this filter." /></td>
              </tr>
            )}
          </tbody>
        </table>
      </TableSurface>

      {/* Mobile card list */}
      <div className="space-y-2 md:hidden">
        {rows.map((row) => (
          <div key={row.campaign_id} className={cn("hx-finance-card", row.payment_status === "overdue" && "hx-finance-card-overdue")}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-slate-900">{row.campaign_name}</div>
                <div className="text-xs text-slate-500">{row.client_name || "Client not set"} · {row.campaign_code}</div>
                <ForwardBadges carriedForward={row.carried_forward} forwardedTo={row.forwarded_to} />
              </div>
              <StatusPill tone={statusTone(row.payment_status)}>{row.payment_status}</StatusPill>
            </div>
            <div className="mt-2.5 grid grid-cols-3 gap-2 border-t border-slate-100 pt-2.5 text-xs">
              <div>
                <div className="text-[10px] uppercase tracking-wide text-slate-400">Expected</div>
                <div className="tabular-nums font-medium text-slate-800">{inr(row.expected_amount)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-slate-400">Received</div>
                <div className="tabular-nums font-medium text-emerald-700">{inr(row.received_in_period || 0)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-slate-400">Pending</div>
                <div className={cn("tabular-nums font-semibold", row.payment_status === "overdue" ? "text-red-700" : "text-slate-950")}>{inr(row.pending_amount)}</div>
              </div>
            </div>
            <div className="mt-2 text-[11px] text-slate-500">
              Due {row.payment_due_date ? fmtDate(row.payment_due_date) : "date not set"}
            </div>
          </div>
        ))}
        {rows.length === 0 && (
          <EmptyState title="No client receivables" description="No receivables match this filter." />
        )}
      </div>
    </div>
  );
}

function RentQueueTable({ rows, loading, selectedDate, onMarkPaid }) {
  const emptyState = (
    <EmptyState
      title={selectedDate ? "No rent due on this date" : "No rent events"}
      description={selectedDate ? "Try another calendar day or clear the selected date." : "No rent payables match this filter."}
    />
  );

  return (
    <div className={cn("hx-inventory-table-wrap hx-rent-table", loading && "is-refreshing")}>
      {/* Desktop / tablet table */}
      <div className="hx-finance-table hidden md:block">
        <table>
          <thead>
            <tr>
              <th className="is-hoarding">Landowner / Hoarding</th>
              <th>Status</th>
              <th>Due Date</th>
              <th>Rent Month</th>
              <th className="text-right">Amount</th>
              <th className="text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((event) => (
              <tr
                key={`${event.hoarding_id}-${event.rent_month}-${event.due_date}`}
                className={event.status === "overdue" ? "hx-row-overdue" : undefined}
              >
                <td>
                  <div className="hx-rent-row-title">
                    <strong title={event.landowner_name || "Landowner not set"}>{event.landowner_name || "Landowner not set"}</strong>
                    <span title={[event.hoarding_code, event.hoarding_name, event.location].filter(Boolean).join(" · ")}>
                      {[event.hoarding_code, event.hoarding_name, event.location].filter(Boolean).join(" · ")}
                    </span>
                  </div>
                  <ForwardBadges carriedForward={event.carried_forward} forwardedTo={event.forwarded_to} />
                </td>
                <td><StatusPill tone={statusTone(event.status)}>{event.status}</StatusPill></td>
                <td className="hx-date-cell">{fmtDate(event.due_date)}</td>
                <td className="hx-date-cell">{formatMonthLabel(event.rent_month)}</td>
                <td className={cn("hx-money-cell text-right", event.status === "overdue" && "font-semibold text-red-700")}>{inr(event.amount)}</td>
                <td className="text-right">
                  {event.status !== "paid" ? (
                    <Button size="sm" variant="outline" className="whitespace-nowrap" onClick={() => onMarkPaid(event)}>
                      <CheckCircle2 size={14} className="text-emerald-600" /> Mark paid
                    </Button>
                  ) : (
                    <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-semibold text-emerald-700">
                      <CheckCircle2 size={13} /> Paid {event.paid_date ? fmtDate(event.paid_date) : ""}
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="py-12 text-center">{emptyState}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile card list */}
      <div className="space-y-2 md:hidden">
        {rows.map((event) => (
          <div
            key={`${event.hoarding_id}-${event.rent_month}-${event.due_date}`}
            className={cn("hx-finance-card", event.status === "overdue" && "hx-finance-card-overdue")}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-slate-900">{event.landowner_name || "Landowner not set"}</div>
                <div className="truncate text-xs text-slate-500">
                  {[event.hoarding_code, event.hoarding_name, event.location].filter(Boolean).join(" · ")}
                </div>
                <ForwardBadges carriedForward={event.carried_forward} forwardedTo={event.forwarded_to} />
              </div>
              <StatusPill tone={statusTone(event.status)}>{event.status}</StatusPill>
            </div>
            <div className="mt-2.5 flex items-end justify-between gap-2 border-t border-slate-100 pt-2.5">
              <div className="text-[11px] text-slate-500">
                <div>Due {fmtDate(event.due_date)}</div>
                <div>{formatMonthLabel(event.rent_month)}</div>
              </div>
              <div className={cn("tabular-nums text-sm font-bold", event.status === "overdue" ? "text-red-700" : "text-slate-950")}>
                {inr(event.amount)}
              </div>
            </div>
            <div className="mt-2.5">
              {event.status !== "paid" ? (
                <Button size="sm" variant="outline" className="w-full" onClick={() => onMarkPaid(event)}>
                  <CheckCircle2 size={14} className="text-emerald-600" /> Mark paid
                </Button>
              ) : (
                <div className="flex items-center justify-center gap-1 rounded-md bg-emerald-50 py-1.5 text-xs font-semibold text-emerald-700">
                  <CheckCircle2 size={13} /> Paid {event.paid_date ? fmtDate(event.paid_date) : ""}
                </div>
              )}
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="py-8">{emptyState}</div>}
      </div>
    </div>
  );
}

function Panel({ title, children, className }) {
  return (
    <div className={cn("hx-surface hx-finance-panel p-4", className)}>
      <h4 className="font-display mb-3 text-sm font-semibold text-slate-950">{title}</h4>
      <div className="hx-finance-panel-body space-y-1.5">{children}</div>
    </div>
  );
}
